import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { DomListenerFactoryService } from 'src/app/services/dom-listener-factory.service';
import { TelemetryService } from 'src/app/services/telemetry.service';
import { enterZone } from 'src/app/utilities/ZoneUtils';

const BROWSER_FULLSCREEN_ERROR_EVENT = 'Browser Fullscreen Request Failed';

/**
 * Puts the browser into native full screen while a video tile is pinned or full-screened.
 *
 * The whole document goes full screen (not the tile element), so the in-app pinned/fullscreen
 * layout, controls and menus keep working and simply get the whole screen.
 *
 * The browser only allows `enter()` shortly after a user gesture. Rejections are reported to
 * telemetry and otherwise ignored: the in-app view still works.
 * Only full screen that this service entered is ever exited by it.
 */
@Injectable()
export class BrowserFullscreenService implements OnDestroy {
  private enteredByApp = false;
  private readonly domListener = this.domListenerFactoryService.createInstance();

  // Emits when the user leaves the full screen we entered through the browser (Esc, browser UI).
  private readonly _exitedByBrowser$ = new Subject<void>();
  readonly exitedByBrowser$ = this._exitedByBrowser$.asObservable().pipe(enterZone(this.zone));

  constructor(
    private zone: NgZone,
    private domListenerFactoryService: DomListenerFactoryService,
    private telemetryService: TelemetryService,
  ) {
    this.domListener.add(document, 'fullscreenchange', () => this.onFullscreenChange(), true);
  }

  get isSupported(): boolean {
    return !!document.fullscreenEnabled && !!document.documentElement.requestFullscreen;
  }

  enter(context: Record<string, unknown> = {}): void {
    // Leave full screen started by something else (e.g. a native <video> full screen) alone.
    if (!this.isSupported || this.enteredByApp || document.fullscreenElement) {
      return;
    }
    // Read before the request: it tells whether a rejection was due to a missing user gesture
    const hadUserActivation = (navigator as Navigator & { userActivation?: { isActive: boolean } })
      .userActivation?.isActive;
    this.enteredByApp = true;
    document.documentElement
      .requestFullscreen()
      .then(() => {
        // exit() was called while the request was still pending
        if (!this.enteredByApp) {
          this.exitDocumentFullscreen();
        }
      })
      .catch((error: unknown) => {
        this.enteredByApp = false;
        this.telemetryService.errorEvent(BROWSER_FULLSCREEN_ERROR_EVENT, {
          ...context,
          hadUserActivation,
          errorName: error instanceof Error ? error.name : undefined,
          errorMessage: error instanceof Error ? error.message : String(error),
        });
      });
  }

  exit(): void {
    if (!this.enteredByApp) {
      return;
    }
    this.enteredByApp = false;
    this.exitDocumentFullscreen();
  }

  ngOnDestroy(): void {
    this.domListener.clear();
    this.exit();
    this._exitedByBrowser$.complete();
  }

  private onFullscreenChange(): void {
    if (document.fullscreenElement || !this.enteredByApp) {
      return;
    }
    this.enteredByApp = false;
    this._exitedByBrowser$.next();
  }

  private exitDocumentFullscreen(): void {
    if (document.fullscreenElement && document.exitFullscreen) {
      document.exitFullscreen().catch(() => undefined);
    }
  }
}
